import {create} from 'zustand/react';
import type {CenterInfo} from '../models';
import callApi from '../apis/callApi.ts';
import type {IDevices, IVideoMode} from '../layouts/menu/types';

interface ICenterStore {
    deviceID: string;
    setDeviceID: (val: string) => void;
    centerInfo: CenterInfo | null;
    getCenterInfo: (val: string) => Promise<void>;
    setCenterInfo: (val: CenterInfo) => void;
    allDevices: Array<IDevices>;
    setAllDevices: () => Promise<void>;
    // 해상도
    videoMode: IVideoMode;
    setVideoMode: (val: IVideoMode) => void;
    // 장치 모두
    cameraEnable: boolean;
    setToggleCameraEnable: () => void;
    videoInputA: Array<IDevices>;
    audioInputA: Array<IDevices>;
    audioOutputA: Array<IDevices>;
    // 실시간 스트리밍(화상회의)
    videoInputD: IDevices | null;
    audioInputD: IDevices | null;
    audioOutputD: IDevices | null;
    // ip-tv
    captureBoardD: IDevices | null;
    captureBoardAudioD: IDevices | null;
    // 장비 세팅함수
    setDatas: (cameraEn: boolean, vI: IDevices, aI: IDevices, aO: IDevices, cbI: IDevices, cbaI: IDevices) => void;
    // 미디어 스트림
    mediaStream: MediaStream | null;
    captureBoardMediaStream: MediaStream | null;
    setMediaStream: (val: MediaStream | null) => void;
    setCaptureBoardMediaStream: (val: MediaStream | null) => void;
    // 일렉트론 버전
    electronVer: string;
    setElectronVer: (val: string) => void;
}

const loadDeviceSettingsFromStorage = () => {
    try {
        const cameraEnable = localStorage.getItem('cameraEnable');
        const videoInputA = localStorage.getItem('videoInputA');
        const audioInputA = localStorage.getItem('audioInputA');
        const audioOutputA = localStorage.getItem('audioOutputA');
        const videoInputD = localStorage.getItem('videoInputD');
        const audioInputD = localStorage.getItem('audioInputD');
        const audioOutputD = localStorage.getItem('audioOutputD');
        const captureBoardD = localStorage.getItem('captureBoardD');
        const captureBoardAudioD = localStorage.getItem('captureBoardAudioD');

        return {
            cameraEnable: cameraEnable ? JSON.parse(cameraEnable) : null,
            videoInputA: videoInputA ? JSON.parse(videoInputA) : null,
            audioInputA: audioInputA ? JSON.parse(audioInputA) : null,
            audioOutputA: audioOutputA ? JSON.parse(audioOutputA) : null,
            videoInputD: videoInputD ? (isCaptureBoard(JSON.parse(videoInputD)) ? null : JSON.parse(videoInputD)) : null,
            audioInputD: audioInputD ? JSON.parse(audioInputD) : null,
            audioOutputD: audioOutputD ? JSON.parse(audioOutputD) : null,
            captureBoardD: captureBoardD ? JSON.parse(captureBoardD) : null,
            captureBoardAudioD: captureBoardAudioD ? JSON.parse(captureBoardAudioD) : null,
        };
    } catch (error) {
        console.error('Error loading device settings from localStorage:', error);
        return {
            cameraEnable: null,
            videoInputA: null,
            audioInputA: null,
            audioOutputA: null,
            videoInputD: null,
            audioInputD: null,
            audioOutputD: null,
            captureBoardD: null,
            captureBoardAudioD: null,
        };
    }
};

const saveDeviceSettingsToStorage = (settings: {
    cameraEnable: boolean;
    videoInputA: Array<IDevices>;
    audioInputA: Array<IDevices>;
    audioOutputA: Array<IDevices>;
    videoInputD: IDevices | null;
    audioInputD: IDevices | null;
    audioOutputD: IDevices | null;
    captureBoardD: IDevices | null;
    captureBoardAudioD: IDevices | null;
}) => {
    try {
        localStorage.setItem('cameraEnable', JSON.stringify(settings.cameraEnable));
        localStorage.setItem('videoInputA', JSON.stringify(settings.videoInputA));
        localStorage.setItem('audioInputA', JSON.stringify(settings.audioInputA));
        localStorage.setItem('audioOutputA', JSON.stringify(settings.audioOutputA));
        localStorage.setItem('videoInputD', JSON.stringify(settings.videoInputD));
        localStorage.setItem('audioInputD', JSON.stringify(settings.audioInputD));
        localStorage.setItem('audioOutputD', JSON.stringify(settings.audioOutputD));
        localStorage.setItem('captureBoardD', JSON.stringify(settings.captureBoardD));
        localStorage.setItem('captureBoardAudioD', JSON.stringify(settings.captureBoardAudioD));
    } catch (error) {
        console.error('Error saving device settings to localStorage:', error);
    }
};

const isCaptureBoard = (device: IDevices | null): boolean => {
    return !!device?.label?.includes('Live Streamer CAP 4K');
};

const findDeviceById = (devices: Array<IDevices>, deviceId: string): IDevices | null => {
    return devices.find((device) => device.deviceId === deviceId) || null;
};

const useCenterStore = create<ICenterStore>((set, get) => {
    const savedSettings = loadDeviceSettingsFromStorage();

    return {
        deviceID: localStorage.getItem('deviceID') || '',
        setDeviceID: (deviceID: string) => {
            localStorage.setItem('deviceID', deviceID);
            set({deviceID: deviceID});
        },
        centerInfo: null,
        getCenterInfo: async (deviceID: string) => {
            const res = await callApi().get(`/center-info/?settop_sn=${deviceID}`);
            set({centerInfo: res.data});
        },
        setCenterInfo: (centerParam: CenterInfo) => {
            set({centerInfo: centerParam});
        },
        videoMode: 'best-perform',
        setVideoMode: (val: IVideoMode) => {
            set({videoMode: val});
        },
        allDevices: [],
        cameraEnable: savedSettings.cameraEnable || true,
        setToggleCameraEnable: () => {
            set({cameraEnable: !get().cameraEnable});
        },
        videoInputA: savedSettings.videoInputA || [],
        audioInputA: savedSettings.audioInputA || [],
        audioOutputA: savedSettings.audioOutputA || [],
        videoInputD: savedSettings.videoInputD,
        audioInputD: savedSettings.audioInputD,
        audioOutputD: savedSettings.audioOutputD,
        captureBoardD: savedSettings.captureBoardD,
        captureBoardAudioD: savedSettings.captureBoardAudioD,
        setAllDevices: async () => {
            if (!navigator?.mediaDevices?.enumerateDevices) {
                console.error('mediaDevices API를 사용할 수 없습니다');
                return;
            }
            try {
                const savedSettings = loadDeviceSettingsFromStorage();
                let connectedDevices = await navigator.mediaDevices.enumerateDevices();
                const hasDevices = connectedDevices.some((d) => d.label !== '');

                if (!hasDevices) {
                    try {
                        let audioStream = null;
                        let videoStream = null;

                        try {
                            audioStream = await navigator.mediaDevices.getUserMedia({audio: true});
                        } catch (audioError) {
                            console.warn('오디오 장치 접근 실패:', audioError);
                        }

                        try {
                            videoStream = await navigator.mediaDevices.getUserMedia({video: true});
                        } catch (videoError) {
                            console.warn('비디오 장치 접근 실패:', videoError);
                        }

                        [audioStream, videoStream].forEach((stream) => {
                            if (stream) {
                                stream.getTracks().forEach((track) => track.stop());
                            }
                        });

                        connectedDevices = await navigator.mediaDevices.enumerateDevices();
                    } catch (error) {
                        console.error('미디어 장치 권한 획득 실패:', error);
                    }
                }

                set({allDevices: connectedDevices});

                const videoInputs = connectedDevices.filter((d) => d.kind === 'videoinput');
                const audioInputs = connectedDevices.filter((d) => d.kind === 'audioinput');
                const audioOutputs = connectedDevices.filter((d) => d.kind === 'audiooutput');

                if (videoInputs.length === 0 && audioInputs.length === 0) {
                    console.warn('사용 가능한 미디어 장치가 없습니다');
                    return;
                }

                const hasAllSavedSettings =
                    savedSettings.cameraEnable !== null &&
                    savedSettings.videoInputA &&
                    savedSettings.audioInputA &&
                    savedSettings.audioOutputA &&
                    savedSettings.videoInputD &&
                    savedSettings.audioInputD &&
                    savedSettings.audioOutputD &&
                    savedSettings.captureBoardD &&
                    savedSettings.captureBoardAudioD;

                let videoInputD: IDevices | null = null;
                let audioInputD: IDevices | null = null;
                let audioOutputD: IDevices | null = null;
                let captureBoardD: IDevices | null = null;
                let captureBoardAudioD: IDevices | null = null;

                if (hasAllSavedSettings) {
                    const savedVideoInput = findDeviceById(videoInputs, savedSettings.videoInputD.deviceId);
                    videoInputD =
                        (savedVideoInput && !isCaptureBoard(savedVideoInput) ? savedVideoInput : null) ||
                        videoInputs.find((d) => d.label.includes('FHD')) ||
                        null;
                    audioInputD =
                        findDeviceById(audioInputs, savedSettings.audioInputD.deviceId) ||
                        audioInputs.find((d) => d.label.includes('NX')) ||
                        audioInputs[0] ||
                        null;
                    audioOutputD =
                        findDeviceById(audioOutputs, savedSettings.audioOutputD.deviceId) ||
                        audioOutputs.find((d) => d.label.includes('HDMI') || d.label.includes('High Definition')) ||
                        audioOutputs[0] ||
                        null;
                    captureBoardD =
                        findDeviceById(videoInputs, savedSettings.captureBoardD.deviceId) ||
                        videoInputs.find((d) => d.label.includes('Live Streamer CAP 4K')) ||
                        videoInputs[0] ||
                        null;
                    captureBoardAudioD =
                        findDeviceById(audioInputs, savedSettings.captureBoardAudioD.deviceId) ||
                        audioInputs.find((d) => d.label.includes('Live Streamer CAP 4K')) ||
                        audioInputs[0] ||
                        null;
                } else {
                    videoInputD =
                        videoInputs.find((d) => d.label.includes('FHD') || d.label.includes('V11')) ||
                        null;
                    captureBoardD =
                        videoInputs.find((d) => d.label.includes('Live Streamer CAP 4K')) || videoInputs[0] || null;
                    audioInputD =
                        audioInputs.find((d) => d.label.includes('FHD') || d.label.includes('V11')) ||
                        audioInputs[0] ||
                        null;
                    captureBoardAudioD =
                        audioInputs.find((d) => d.label.includes('Live Streamer CAP 4K')) || audioInputs[0] || null;
                    audioOutputD =
                        audioOutputs.find((d) => d.label.includes('HDMI') || d.label.includes('High Definition')) ||
                        audioOutputs[0] ||
                        null;
                }

                const newSettings = {
                    cameraEnable: savedSettings.cameraEnable ?? true,
                    videoInputA: videoInputs,
                    audioInputA: audioInputs,
                    audioOutputA: audioOutputs,
                    videoInputD,
                    audioInputD,
                    audioOutputD,
                    captureBoardD,
                    captureBoardAudioD,
                };
                set(newSettings);
                saveDeviceSettingsToStorage(newSettings);
            } catch (error) {
                console.error('장치 조회 중 오류 발생:', error);
            }
        },
        setDatas: (cameraEn: boolean, vI: IDevices, aI: IDevices, aO: IDevices, cbI: IDevices, cbaI: IDevices) => {
            const newSettings = {
                cameraEnable: cameraEn,
                videoInputD: vI,
                audioInputD: aI,
                audioOutputD: aO,
                captureBoardD: cbI,
                captureBoardAudioD: cbaI,
            };
            set(newSettings);
            const currentState = get();
            saveDeviceSettingsToStorage({
                ...newSettings,
                videoInputA: currentState.videoInputA,
                audioInputA: currentState.audioInputA,
                audioOutputA: currentState.audioOutputA,
            });
        },
        mediaStream: null as MediaStream | null,
        captureBoardMediaStream: null as MediaStream | null,
        setMediaStream: (stream: MediaStream | null) => {
            const currentStream = get().mediaStream;
            if (currentStream) {
                currentStream.getTracks().forEach((track) => track.stop());
            }
            set({mediaStream: stream});
        },
        setCaptureBoardMediaStream: (stream: MediaStream | null) => {
            const currentStream = get().captureBoardMediaStream;
            if (currentStream) {
                currentStream.getTracks().forEach((track) => track.stop());
            }
            set({captureBoardMediaStream: stream});
        },
        electronVer: "",
        setElectronVer: (val: string) => {
            set({electronVer: val})
        }
    };
});

export default useCenterStore;
